import { useState, useMemo } from 'react';
import ColumnHeader from './ColumnHeader';
import { Table } from 'reactstrap';
import Spinner from '../UISpinner/UISpinner';

function SortableTable({ data, columns, isLoading, rowStyle, loaderText }) {
    const [sortConfig, setSortConfig] = useState(() => {
        const value = columns
            .filter(x => !!x.initialSort)
            .map(x => ({ key: x.property, direction: x.initialSort }));

        return value;
    });

    const handleSort = (key, isMultiSort) => {
        let direction = 'asc';
        const existingSort = sortConfig.find(item => item.key === key);
        if (existingSort) {
            direction = existingSort.direction === 'asc' ? 'desc' : null;
        }

        let newSortConfig = [];

        if (isMultiSort) {
            newSortConfig = sortConfig.filter(item => item.key !== key);
        }

        if (direction) {
            newSortConfig.push({ key, direction });
        }
        setSortConfig(newSortConfig);
    };

    const sortedData = useMemo(() => {
        const source = Array.isArray(data) ? data : [];
        let sortableData = [...source];

        if (sortConfig.length > 0) {
            sortableData.sort((a, b) => {
                for (const { key, direction } of sortConfig) {
                    if (!a[key] && b[key]) {
                        return direction === 'asc' ? -1 : 1;
                    }
                    if (a[key] && !b[key]) {
                        return direction === 'desc' ? -1 : 1;
                    }
                    if (a[key] < b[key]) {
                        return direction === 'asc' ? -1 : 1;
                    }
                    if (a[key] > b[key]) {
                        return direction === 'desc' ? -1 : 1;
                    }
                }
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    const generateCell = (row, column) => {
        if (column.custom) {
            return column.custom(row[column.property], row);
        } else {
            return row[column.property];
        }
    };

    const hasRows = sortedData && sortedData.length > 0;

    return (
        <div
            className="table-responsive"
            style={{
                height: '400px',
                overflowY: 'auto',
                position: 'relative',
            }}
        >
            <Table size="sm" striped>
                <thead
                    className="sticky-top thead-light"
                    style={{ top: 0, background: 'white', zIndex: 2 }}
                >
                    <tr>
                        {columns.map((column, index) => (
                            <ColumnHeader
                                key={`header-${index}`}
                                property={column.property}
                                sortable={column.sortable}
                                sortDirection={
                                    sortConfig.find(
                                        (item) => item.key === column.property
                                    )?.direction
                                }
                                onSort={(event) =>
                                    handleSort(column.property, event.shiftKey)
                                }
                            >
                                {column.header}
                            </ColumnHeader>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isLoading && (
                        <tr>
                            <td colSpan={columns.length} className="p-0">
                                <div className="table-loader-container d-flex flex-column justify-content-center align-items-center">
                                    <Spinner text={loaderText ?? 'Loading...'} />
                                </div>
                            </td>
                        </tr>
                    )}

                    {!isLoading && hasRows &&
                        sortedData.map((row, index) => (
                            <tr
                                key={`${index}_table_row`}
                                className={`vertAlign ${rowStyle?.(row)}`}
                            >
                                {columns.map((column, colIndex) => (
                                    <td
                                        key={`${index}_${colIndex}_row_data`}
                                        className="middleVertAlign"
                                    >
                                        {generateCell(row, column)}
                                    </td>
                                ))}
                            </tr>
                        ))}

                    {!isLoading && !hasRows && (
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="text-center py-3"
                            >
                                No results found
                            </td>
                        </tr>
                    )}

                    <tr style={{ height: '12px' }} aria-hidden="true"></tr>
                </tbody>
            </Table>
        </div>
    );
}

export default SortableTable;
